import { Modal } from "./Modal.js";
import type { CommandOutcome } from "../types/api.js";

/** How a command outcome is shown to a human.
 *
 * The API answers 200 whether or not the door moved, so `commandSuccessful` decides this, not
 * the HTTP status. And a `critical` outcome means a door may be physically held unlocked RIGHT
 * NOW - that gets a modal which does NOT auto-dismiss, names the door, shows the correlation id
 * and tells the operator to go and look. A toast that fades after four seconds is the wrong
 * component for "a door may be standing open". */
export function OutcomeDialog({
  outcome,
  onClose,
}: {
  outcome: CommandOutcome;
  onClose: () => void;
}) {
  const critical = outcome.severity === "critical";

  if (outcome.commandSuccessful && !critical) {
    return (
      <Modal
        title={`${outcome.door.name}: done`}
        onClose={onClose}
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        }
      >
        <p>{outcome.reason}</p>
        <p className="modal-meta">
          Correlation id <code>{outcome.correlationId}</code>
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        critical
          ? `${outcome.door.name}: GO AND LOOK AT THIS DOOR`
          : `${outcome.door.name}: the command did not succeed`
      }
      onClose={onClose}
      tone={critical ? "critical" : "normal"}
      dismissible={!critical}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          I have read this
        </button>
      }
    >
      {critical ? (
        <p>
          <strong>This door may be physically held unlocked right now.</strong> Verification may be
          suspended, which means anyone can walk through it. Go and check the door in person before
          doing anything else.
        </p>
      ) : null}

      <p>{outcome.reason}</p>

      {!outcome.commandSent ? (
        <p>
          Nothing was sent to the door. The command was validated, authorised and recorded only.
        </p>
      ) : null}

      <p className="modal-meta">
        Door <code>{outcome.doorId}</code> · state <code>{outcome.door.currentState}</code> ·
        correlation id <code>{outcome.correlationId}</code>
      </p>
    </Modal>
  );
}
